

//import { Textarea } from "@/components/ui/textarea"
//import { Badge } from "@/components/ui/badge"
//import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { toast } from "sonner"
import { usePostCandidates, usePostCandidatesResume, usePostContactsCandidate, usePostFilesOwnerOwnerId } from "openapi/api/endpoints"
import { useQueryClient } from "@tanstack/react-query"
import { AddCandidateDialogReusable, LinkedInFormData, mapResumeFormToApiCandidate, ResumeFormData } from "../reusable/add-candidates-dialog"
import { splitCandidateName } from "@/utils/candidate-name"

interface Cause {
  code?: string
  details?: string
  hint?: string | null
  message: string | null
}

interface ErrorWithCause extends Error {
  cause?: Cause
}

export class AppError extends Error implements ErrorWithCause {
  status: number
  code: string
  cause?: Cause

  constructor(
    message: string,
    status = 500,
    code = 'INTERNAL_ERROR',
    cause?: Cause
  ) {
    super(message)
    this.status = status
    this.code = code
    this.cause = cause
  }
}

type AddCandidateDialogProps = {
  openFromUrl?: boolean
  onClearUrlOpen?: () => void
  /** See `AddCandidateDialogReusable` — the page header's create is a pill. */
  triggerTone?: "primary" | "teal"
}

export function AddCandidateDialog({
  openFromUrl,
  onClearUrlOpen,
  triggerTone,
}: AddCandidateDialogProps = {}) {
  const queryClient = useQueryClient()
  const { mutateAsync: uploadFile } = usePostFilesOwnerOwnerId()
  const {mutateAsync:createContact}=usePostContactsCandidate()
  const { mutateAsync: lkdn_url } = usePostCandidates()

  const { mutateAsync: addCandidate } = usePostCandidatesResume()

    const onLinkedInSubmit = async (url: LinkedInFormData) => {
      try {
        const data=await lkdn_url({ data: { linkedin_url: url.linkedinUrl } })
        const candidateName = data.candidate?.full_name || 'Candidate'
        toast.success("Candidate added!",{
          description: `${candidateName} has been added successfully.`,
          action: {
            label: "Dismiss",
            onClick: () => {},
          },
          style: {
            background: '#a3e635'
          }
        })
      }catch (error) {
        toast.error("An error occured!",{
          description: (error as Error)?.message || "Failed to add candidate. Please try again.",
          action: {
            label: "Dismiss",
            onClick: () => {},
          },
          style: {
            background: "#dc2626",
          },
        });
      }finally{
        await queryClient.invalidateQueries({ queryKey: [`/candidates`], exact: false })
        await queryClient.refetchQueries({ queryKey: [`/candidates`], exact: false })
      }  
    }
  
    const onResumeSubmit = async (data: ResumeFormData, resumeFile: File) => {
      let candidateId: string | null = null
      let candidateName: string | undefined
      let createdNewCandidate = false
      try {
        const resumeData=mapResumeFormToApiCandidate(data)
        try {
          const result = await addCandidate({ data: resumeData })
          candidateId = result.candidate?.id??''
          candidateName = result.candidate?.full_name
          createdNewCandidate = true // ✅ important
        } catch (err:any) {
          if (err.response.data.error.code == 'CANDIDATE_ALREADY_ADDED') {
            // ✅ recover only for upload
            const id = err.response.data.error.cause?.details 
            console.log('Err: ',err)
            if (!id) {
              throw new Error('Duplicate candidate error missing candidate_id')
            }

            candidateId = id
            createdNewCandidate = false // ❌ do NOT create contact
            toast.warning("Warning!",{
              description: err.response.data.error.cause?.message,
              duration: 10000,
              action: {
                label: "Dismiss",
                onClick: () => {},
              },
              style: {
                background: "#fffb00ff",
              },
            });
          } else {
            toast.error("An error occured!",{
              description: err.response.data.error.cause?.message,
              duration: 10000,
              action: {
                label: "Dismiss",
                onClick: () => {},
              },
              style: {
                background: "#dc2626",
              },
            });
          }
        }
        console.log('CandidateId: ',candidateId)
        if (!candidateId) {
          throw new Error('Candidate id unavailable')
        }

        // ✅ upload runs for success OR duplicate
        await uploadFile({
          owner: 'candidate',
          ownerId: candidateId,
          data: {
            file: resumeFile,
            name: 'resume'
          }
        })

        // ✅ create contact ONLY if candidate was newly created
        if (createdNewCandidate) {
          await createContact({
            data: {
              contact_type: 'candidate',
              first_name: candidateName ?? '',
              email: resumeData.email,
              phone: resumeData.phone_numbers?.[0] ?? '',
              candidate_id: candidateId,
              source: 'resume'
            }
          })
        }

        toast.success('Resume processed!', {
          description: createdNewCandidate
            ? `${candidateName ?? 'Candidate'} added successfully.`
            : 'Resume uploaded to existing candidate.',
          style: { background: '#a3e635' }
        })
      } catch (error) {
        toast.error("An error occured!",{
          description: (error as Error)?.message || "Failed to add candidate. Please try again.",
          duration: 7000,
          action: {
            label: "Dismiss",
            onClick: () => {},
          },
          style: {
            background: "#dc2626",
          },
        });
      }finally{
        await queryClient.invalidateQueries({ queryKey: [`/candidates`], exact: false })
        await queryClient.refetchQueries({ queryKey: [`/candidates`], exact: false })
      }
    }

    const onManualSubmit = async (data: ResumeFormData) => {
      try {
        const candidateData = mapResumeFormToApiCandidate(data)
        const result = await addCandidate({ data: candidateData })
        const candidateId = result.candidate?.id ?? ""
        const candidateName = result.candidate?.full_name ?? data.full_name

        if (!candidateId) {
          throw new Error("Candidate id unavailable")
        }

        const { firstName, lastName } = splitCandidateName(candidateName)
        await createContact({
          data: {
            contact_type: "candidate",
            first_name: firstName,
            last_name: lastName || undefined,
            email: candidateData.email,
            phone: candidateData.phone_numbers?.[0] ?? "",
            candidate_id: candidateId,
            source: "resume",
          },
        })

        toast.success("Candidate added!", {
          description: `${candidateName || "Candidate"} has been added successfully.`,
          style: {
            background: "#a3e635",
          },
        })
      } catch (error) {
        toast.error("An error occured!", {
          description: (error as Error)?.message || "Failed to add candidate. Please try again.",
          duration: 7000,
          action: {
            label: "Dismiss",
            onClick: () => {},
          },
          style: {
            background: "#dc2626",
          },
        })
      } finally {
        await queryClient.invalidateQueries({ queryKey: [`/candidates`], exact: false })
        await queryClient.refetchQueries({ queryKey: [`/candidates`], exact: false })
      }
    }

  return <AddCandidateDialogReusable
    submiResumeData={onResumeSubmit}
    submitLinkedInData={onLinkedInSubmit}
    submitManualData={onManualSubmit}
    openFromUrl={openFromUrl}
    onClearUrlOpen={onClearUrlOpen}
    triggerTone={triggerTone}
  />
}
